/**
 * SDDViewer Component
 *
 * Display a complete SDD document with collapsible sections and export options
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FileText,
  Download,
  AlertCircle,
  CheckCircle2,
  Clock,
  Archive,
  XCircle,
  Send,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeMarkdownHtml } from '@/lib/sanitize';
import { useSDD, useExportSDD } from '@/hooks/useSDD';
import { useToast } from '@/shared/hooks/useToast';
import type { SDDDocumentStatus } from '@forgenetwork/types';

interface SDDViewerProps {
  repoId: string;
  sddId: string;
  onExport?: () => void;
}

/**
 * Status badge variants and icons
 */
const statusConfig: Record<
  SDDDocumentStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
  pending_review: { variant: 'outline', icon: Send, label: 'Pending Review' },
  approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
  rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
  archived: { variant: 'outline', icon: Archive, label: 'Archived' },
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Parse SDD content - handles both JSON structured content and plain markdown
 */
function parseSDDContent(content: string): {
  sections: { id: string; title: string; content: string; level: number }[];
  metadata?: Record<string, unknown>;
} {
  try {
    const parsed = JSON.parse(content);
    if (parsed.sections && Array.isArray(parsed.sections)) {
      return {
        sections: parsed.sections,
        metadata: parsed.metadata,
      };
    }
    // Content is JSON but not structured - wrap as single section
    return {
      sections: [{
        id: 'content',
        title: 'Content',
        content: typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2),
        level: 1,
      }],
    };
  } catch {
    // Content is plain markdown - parse headings
    const lines = content.split('\n');
    const sections: { id: string; title: string; content: string; level: number }[] = [];
    let currentSection: { id: string; title: string; content: string; level: number } | null = null;
    let contentLines: string[] = [];

    for (const line of lines) {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch && headingMatch[1] && headingMatch[2]) {
        // Save previous section
        if (currentSection) {
          currentSection.content = contentLines.join('\n').trim();
          sections.push(currentSection);
        }
        // Start new section
        const level = headingMatch[1].length;
        const title = headingMatch[2];
        currentSection = {
          id: `section-${sections.length}`,
          title,
          content: '',
          level,
        };
        contentLines = [];
      } else if (currentSection) {
        contentLines.push(line);
      } else {
        // Content before first heading - create intro section
        if (line.trim()) {
          if (!currentSection) {
            currentSection = {
              id: 'intro',
              title: 'Overview',
              content: '',
              level: 1,
            };
          }
          contentLines.push(line);
        }
      }
    }

    // Save last section
    if (currentSection) {
      currentSection.content = contentLines.join('\n').trim();
      sections.push(currentSection);
    }

    // If no sections found, wrap entire content
    if (sections.length === 0) {
      return {
        sections: [{
          id: 'content',
          title: 'Content',
          content: content,
          level: 1,
        }],
      };
    }

    return { sections };
  }
}

/**
 * SDDViewer - Display a complete SDD document
 */
export function SDDViewer({ repoId, sddId, onExport }: SDDViewerProps): React.JSX.Element {
  const { data: sdd, isLoading, error } = useSDD(repoId, sddId);
  const exportSDD = useExportSDD();
  const { toast } = useToast();

  const handleExport = async (format: 'markdown' | 'json' | 'html'): Promise<void> => {
    try {
      await exportSDD.mutateAsync({
        repoId,
        sddId,
        options: {
          format,
          includeTableOfContents: true,
          includeMetadata: true,
        },
      });
      toast({
        title: 'Export Successful',
        description: `SDD exported as ${format.toUpperCase()}`,
      });
      onExport?.();
    } catch (err) {
      toast({
        title: 'Export Failed',
        description: err instanceof Error ? err.message : 'Failed to export SDD',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
        <div className="h-64 bg-muted animate-pulse rounded-xl" />
        <div className="h-96 bg-muted animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error instanceof Error ? error.message : 'Failed to load SDD'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!sdd) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">SDD not found</p>
        </CardContent>
      </Card>
    );
  }

  const statusInfo = statusConfig[sdd.status];
  const StatusIcon = statusInfo.icon;
  const parsedContent = parseSDDContent(sdd.content);

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <Card className="print:shadow-none">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-6 w-6 text-primary" />
                <CardTitle className="text-3xl">{sdd.title}</CardTitle>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <Badge variant={statusInfo.variant} className="gap-1">
                  <StatusIcon className="h-3 w-3" />
                  {statusInfo.label}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Version {sdd.version}
                </span>
                <span className="text-sm text-muted-foreground">
                  Updated {formatDate(sdd.updatedAt)}
                </span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2 print:hidden"
                  disabled={exportSDD.isPending}
                >
                  <Download className="h-4 w-4" />
                  Export
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('markdown')}>
                  Export as Markdown
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('json')}>
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('html')}>
                  Export as HTML
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
      </Card>

      {/* Metadata */}
      {parsedContent.metadata && (
        <Card className="print:shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Document Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Created</span>
                <p className="font-medium">{formatDate(sdd.createdAt)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Last Updated</span>
                <p className="font-medium">{formatDate(sdd.updatedAt)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Version</span>
                <p className="font-medium">{sdd.version}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <p className="font-medium capitalize">{sdd.status.replace('_', ' ')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <Card className="print:shadow-none">
        <CardContent className="pt-6">
          <div className="space-y-8">
            {parsedContent.sections.map((section) => (
              <div key={section.id} className="space-y-3">
                <h2
                  className={cn(
                    'font-semibold',
                    section.level === 1 && 'text-2xl',
                    section.level === 2 && 'text-xl',
                    section.level >= 3 && 'text-lg'
                  )}
                >
                  {section.title}
                </h2>
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  {/* SECURITY: Sanitize HTML to prevent XSS from backend content */}
                  <div
                    className="whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: sanitizeMarkdownHtml(section.content) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
